import { type FormEvent, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle, CreditCard, Receipt, ShieldCheck, UserRound, XCircle } from "lucide-react";
import { api, type AppSettings, type Membership, type MembershipPeriod, type MembershipPlan, type Order, type PointBalance, type PointEvent, type PointPackagePlan, type User } from "../services/api";

type BillingPageProps = {
  user: User | null;
  isPreview?: boolean;
  appSettings: AppSettings;
  purchaseEnabled: boolean;
  onAuthOpen: () => void;
  onLaunchNotice: () => void;
  onLogout: () => void;
  onUserUpdate: (user: User) => void;
  navigate: (path: string) => void;
};

// TEMP (account page simplification, 2026-07-02):
// Keep the detailed account sections in source for later re-enabling, but hide
// them from the customer-facing account page while the UI focuses on overview.
const SHOW_ACCOUNT_DETAIL_SECTIONS = false;

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "-";
}

function formatPoints(value: number | null | undefined) {
  return typeof value === "number" ? value.toLocaleString() : "-";
}

function pointPercent(available: number, total: number) {
  if (!Number.isFinite(available) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((available / total) * 100)));
}

function formatPointPackageTimeRemaining(expiresAt: string) {
  const expiresTime = new Date(expiresAt).getTime();
  const diffMs = expiresTime - Date.now();
  if (!Number.isFinite(expiresTime) || diffMs <= 0) return "已到期";

  const diffMinutes = Math.ceil(diffMs / 60000);
  if (diffMinutes < 60) return `${diffMinutes} 分钟后到期`;

  const diffHours = Math.ceil(diffMinutes / 60);
  if (diffHours < 48) return `${diffHours} 小时后到期`;

  const diffDays = Math.ceil(diffHours / 24);
  return `${diffDays} 天后到期`;
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    PENDING: "待支付",
    PAID: "已支付",
    CLOSED: "已关闭",
    FAILED: "支付失败",
    REFUNDED: "已退款",
    ACTIVE: "生效中",
    EXPIRED: "已过期",
    CANCELLED: "已取消",
    TRIALING: "试用中",
    NONE: "未订阅",
    DISABLED: "已禁用",
    DELETED: "已删除"
  };
  return labels[status] || status;
}

function pointEventLabel(type: string) {
  const labels: Record<string, string> = {
    MEMBERSHIP_DAILY_GRANTED: "会员每日额度",
    POINT_PACKAGE_GRANTED: "加油包到账",
    ADMIN_POINT_GRANTED: "人工加积分",
    ADMIN_POINT_DEBITED: "人工扣积分",
    POINT_USED: "插件消耗",
    MIGRATED_POINT_OPENING: "期初余额"
  };
  return labels[type] || type;
}

function PointUsageBar(props: { label: string; available: number; total: number; meta?: string }) {
  const percent = pointPercent(props.available, props.total);

  return (
    <div className="token-usage-item">
      <div className="token-usage-head">
        <span>{props.label}</span>
        <strong>{percent}%</strong>
      </div>
      <div className="token-usage-bar" aria-label={`${props.label} 剩余 ${percent}%`}>
        <span style={{ width: `${percent}%` }} />
      </div>
      <div className="token-usage-meta">
        <span>剩余 {formatPoints(props.available)} 积分</span>
        <span>总额 {formatPoints(props.total)} 积分</span>
      </div>
      {props.meta ? <p>{props.meta}</p> : null}
    </div>
  );
}

function userDisplayName(user: User) {
  return user.nickname || user.email || user.phone || "Account";
}

const previewUser: User = {
  id: "preview-user",
  email: "preview@example.com",
  phone: "13800000000",
  nickname: "预览用户",
  emailVerified: true,
  phoneVerified: false,
  role: "USER",
  status: "ACTIVE",
  termsAcceptedAt: "2026-06-16T00:00:00.000Z",
  privacyAcceptedAt: "2026-06-16T00:00:00.000Z",
  agreementVersion: "v1",
  createdAt: "2026-06-16T00:00:00.000Z",
  lastLoginAt: "2026-06-16T09:30:00.000Z"
};

const previewPointBalance: PointBalance = {
  balancePoints: 10,
  reservedPoints: 0,
  availablePoints: 10,
  totalGrantedPoints: 10,
  totalUsedPoints: 0,
  pointUsedPoints: 0,
  bucketUsedPoints: 0,
  currentMonthUsedPoints: 0,
  membershipDailyLimit: 10,
  membershipDailyUsedPoints: 0,
  membershipDailyReservedPoints: 0,
  membershipDailyAvailablePoints: 10,
  packageAvailablePoints: 0,
  nextExpiringPackage: null,
  buckets: [],
  updatedAt: "2026-06-16T09:30:00.000Z"
};

function BillingShell(props: BillingPageProps & { title: string; kicker: string; pageClassName?: string; children: React.ReactNode }) {
  // Billing pages share this compact app shell instead of reusing the marketing
  // homepage hero. The payment/member workflows should feel operational and
  // scannable: users need to see account state, order state, and test actions.
  return (
    <main className={`billing-shell${props.pageClassName ? ` ${props.pageClassName}` : ""}`}>
      <header className="billing-header">
        <a className="brand" href="/" onClick={(event) => {
          event.preventDefault();
          props.navigate("/");
        }}>
          <span className="brand-name">MAIPO</span>
        </a>
        <nav className="billing-nav" aria-label="会员导航">
          <a href="/pricing" onClick={(event) => {
            event.preventDefault();
            props.navigate("/pricing");
          }}>会员套餐</a>
          <a href="/account" onClick={(event) => {
            event.preventDefault();
            props.navigate("/account");
          }}>个人主页</a>
        </nav>
        <div className="billing-header-actions">
          {props.user ? (
            <>
              <span>{userDisplayName(props.user)}</span>
              <button className="button button-secondary" type="button" onClick={props.onLogout}>退出</button>
            </>
          ) : (
            <button className="button button-primary" type="button" onClick={props.onAuthOpen}>登录</button>
          )}
        </div>
      </header>
      <div className="billing-page-panel">
        <section className="billing-page-head">
          <span className="eyebrow">{props.kicker}</span>
          <h1>{props.title}</h1>
        </section>
        {props.children}
      </div>
    </main>
  );
}

function PaymentModeNotice(props: { paymentMode: AppSettings["paymentMode"] }) {
  if (props.paymentMode !== "live") return null;

  // The admin can expose the future live state before payment credentials are
  // ready. Customer-facing payment buttons still show the launch notice instead
  // of creating a test order.
  return (
    <section className="billing-preview-notice" aria-label="支付模式提示">
      <AlertTriangle size={18} />
      <div>
        <strong>正式支付暂未接入</strong>
        <p>在线支付正在接入中，点击购买时会提示敬请期待，不会产生真实扣款。</p>
      </div>
    </section>
  );
}

export function PricingPage(props: BillingPageProps) {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [pointPackages, setPointPackages] = useState<PointPackagePlan[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.membershipPlans(), api.pointPackagePlans()])
      .then(([membershipResult, pointPackageResult]) => {
        setPlans(membershipResult.plans);
        setPointPackages(pointPackageResult.plans);
      })
      .catch((error) => {
        setMessage(error instanceof Error ? error.message : "套餐加载失败");
      });
  }, []);

  const visibleMembershipPlans = useMemo(() => {
    return plans.filter((plan) => plan.code !== "free_member");
  }, [plans]);

  function buy() {
    setMessage(null);
    props.onLaunchNotice();
  }

  return (
    <BillingShell {...props} title="套餐与加油包" kicker="Membership and credits" pageClassName="pricing-page-shell">
      <PaymentModeNotice paymentMode={props.appSettings.paymentMode} />
      {message ? <p className="billing-message">{message}</p> : null}
      <h2 className="billing-section-title">会员套餐</h2>
      <section className="billing-plan-grid">
        {visibleMembershipPlans.map((plan) => (
          <article className="billing-card billing-plan-card" key={plan.id}>
            <span className="billing-chip">{plan.durationDays} 天</span>
            <h2>{plan.name}</h2>
            <strong className="billing-price">{plan.priceLabel}</strong>
            <p>每日 {formatPoints(plan.dailyPointLimit)} 积分</p>
            <p>{plan.description}</p>
            <ul>
              {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
            <button className="button button-primary" type="button" onClick={buy}>
              {plan.priceCents === 0 ? "开通免费会员" : "购买会员"}
            </button>
          </article>
        ))}
      </section>
      <h2 className="billing-section-title">积分加油包</h2>
      <section className="billing-plan-grid">
        {pointPackages.map((plan) => (
          <article className="billing-card billing-plan-card" key={plan.id}>
            <span className="billing-chip">{plan.validityDays} 天有效</span>
            <h2>{plan.name}</h2>
            <strong className="billing-price">{plan.priceLabel}</strong>
            <p>{formatPoints(plan.pointAmount)} 积分</p>
            <p>{plan.description}</p>
            <button className="button button-primary" type="button" onClick={buy}>
              购买加油包
            </button>
          </article>
        ))}
      </section>
    </BillingShell>
  );
}

export function AccountPage(props: BillingPageProps) {
  const accountUser = props.isPreview ? previewUser : props.user;
  const shellProps: BillingPageProps = props.isPreview ? { ...props, user: null } : props;
  const [membership, setMembership] = useState<Membership | null>(null);
  const [periods, setPeriods] = useState<MembershipPeriod[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [pointBalance, setPointBalance] = useState<PointBalance | null>(props.isPreview ? previewPointBalance : null);
  const [pointEvents, setPointEvents] = useState<PointEvent[]>([]);
  const [pointEventsExpanded, setPointEventsExpanded] = useState(false);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [profileNickname, setProfileNickname] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    // Account center is the customer-facing verification page. It loads the
    // current membership and the current user's orders independently from the
    // homepage CMS so payment failures do not disturb public content rendering.
    if (props.isPreview || !props.user) {
      setMembership(null);
      setPeriods([]);
      setOrders([]);
      setPointBalance(props.isPreview ? previewPointBalance : null);
      setPointEvents([]);
      setPointEventsExpanded(false);
      setMessage(null);
      return;
    }

    Promise.all([api.membership(), api.membershipPeriods(), api.orders(), api.pointBalance(), api.pointEvents()])
      .then(([membershipResult, periodResult, orderResult, pointResult, pointEventResult]) => {
        setMembership(membershipResult.membership);
        setPeriods(periodResult.periods);
        setOrders(orderResult.orders);
        setPointBalance(pointResult.balance);
        setPointEvents(pointEventResult.events);
        setPointEventsExpanded(false);
      })
      .catch((error) => setMessage(error instanceof Error ? error.message : "账户数据加载失败"));
  }, [props.isPreview, props.user]);

  const pointPackageBuckets = useMemo(() => {
    return (pointBalance?.buckets || []).filter((bucket) => bucket.source === "POINT_PACKAGE");
  }, [pointBalance]);
  const totalPointCapacity = useMemo(() => {
    if (!pointBalance) return 0;
    const bucketTotal = pointBalance.buckets.reduce((sum, bucket) => sum + bucket.totalPoints, 0);
    return bucketTotal > 0 ? bucketTotal : Math.max(pointBalance.availablePoints, pointBalance.balancePoints);
  }, [pointBalance]);
  const packagePointCapacity = useMemo(() => {
    return pointPackageBuckets.reduce((sum, bucket) => sum + bucket.totalPoints, 0);
  }, [pointPackageBuckets]);
  const visiblePointEvents = pointEventsExpanded ? pointEvents : pointEvents.slice(0, 3);
  const primaryMembershipFeatures = membership?.plan.features.slice(0, 2) || [];

  function openProfileEditor() {
    if (!accountUser) return;
    setProfileNickname(accountUser.nickname || "");
    setProfilePhone(accountUser.phone || "");
    setProfileMessage(null);
    setProfileEditorOpen(true);
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const result = await api.updateProfile({
        nickname: profileNickname.trim() || null,
        phone: profilePhone.trim() || null
      });
      props.onUserUpdate(result.user);
      setProfileEditorOpen(false);
    } catch (error) {
      setProfileMessage(error instanceof Error ? error.message : "个人信息保存失败。");
    } finally {
      setProfileSaving(false);
    }
  }

  return (
    <BillingShell {...shellProps} title="个人主页" kicker="Account center" pageClassName="account-page-shell">
      {!accountUser ? (
        <section className="billing-card billing-empty-state">
          <UserRound size={28} />
          <h2>登录后查看会员状态</h2>
          <p>个人主页用于验证会员状态、订单记录和后续权益展示。</p>
          <button className="button button-primary" type="button" onClick={props.onAuthOpen}>登录</button>
        </section>
      ) : (
        <>
          {props.isPreview ? (
            <section className="billing-preview-notice" aria-label="预览模式">
              <AlertTriangle size={20} />
              <div>
                <strong>预览模式</strong>
                <p>当前内容为前端假数据，仅用于提前查看个人中心布局；未连接真实账号、订单或会员数据。</p>
              </div>
            </section>
          ) : null}
          {message ? <p className="billing-message">{message}</p> : null}
          <section className="billing-grid two account-overview-grid">
            <article className="billing-card account-profile-card">
              <div className="account-card-title-row">
                <UserRound size={24} />
                <h2>账号资料</h2>
              </div>
              <strong className="account-profile-name">{accountUser.nickname || accountUser.email || accountUser.phone || "未命名账号"}</strong>
              <dl className="account-detail-list">
                <div><dt>邮箱</dt><dd>{accountUser.email || "-"}</dd></div>
                <div><dt>手机号</dt><dd>{accountUser.phone || "-"}</dd></div>
                <div><dt>账号状态</dt><dd>{statusLabel(accountUser.status)}</dd></div>
              </dl>
              {!props.isPreview ? (
                <button className="button button-secondary account-logout-button" type="button" onClick={openProfileEditor}>修改信息</button>
              ) : null}
            </article>
            <article className="billing-card account-profile-card">
              <div className="account-card-title-row">
                <ShieldCheck size={24} />
                <h2>会员状态</h2>
              </div>
              {membership ? (
                <div className="account-membership-summary">
                  <strong className="account-membership-plan">
                    {membership.plan.name}
                    <span>{statusLabel(membership.status)}</span>
                  </strong>
                  <span>到期时间：{formatDate(membership.expiresAt)}</span>
                  {primaryMembershipFeatures.length > 0 ? (
                    <ul>{primaryMembershipFeatures.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                  ) : null}
                </div>
              ) : (
                <p>暂未开通会员。</p>
              )}
              <button className="button button-secondary" type="button" onClick={() => props.navigate("/pricing")}>查看会员套餐</button>
            </article>
          </section>
          <section className="billing-grid one account-token-grid">
            <article className="billing-card account-token-card">
              <h2>积分</h2>
              {pointBalance ? (
                <>
                  <div className="account-token-overview">
                    <PointUsageBar label="总可用" available={pointBalance.availablePoints} total={totalPointCapacity} />
                    <PointUsageBar label="会员今日剩余" available={pointBalance.membershipDailyAvailablePoints} total={pointBalance.membershipDailyLimit} />
                    <PointUsageBar label="加油包合计剩余" available={pointBalance.packageAvailablePoints} total={packagePointCapacity} />
                  </div>
                  <div className="account-point-packages">
                    <h3>加油包明细</h3>
                    {pointPackageBuckets.length > 0 ? (
                      <div className="account-token-package-list">
                        {pointPackageBuckets.map((bucket, index) => (
                          <PointUsageBar
                            key={bucket.id}
                            label={`加油包 ${index + 1}`}
                            available={bucket.availablePoints}
                            total={bucket.totalPoints}
                            meta={`${formatPointPackageTimeRemaining(bucket.expiresAt)} · 到期 ${formatDate(bucket.expiresAt)}${bucket.reservedPoints > 0 ? ` · 已预留 ${formatPoints(bucket.reservedPoints)} 积分` : ""}`}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="account-token-empty">暂无有效加油包。</p>
                    )}
                  </div>
                </>
              ) : (
                <p>正在加载积分余额。</p>
              )}
              <button className="button button-secondary" type="button" onClick={() => props.navigate("/pricing")}>购买暂未开放</button>
            </article>
          </section>
          {SHOW_ACCOUNT_DETAIL_SECTIONS ? (
            <>
              <section className="billing-card account-secondary-card account-point-events-card">
                <div className="account-secondary-head">
                  <h2>积分明细</h2>
                  {pointEvents.length > 3 ? (
                    <button className="account-secondary-toggle" type="button" onClick={() => setPointEventsExpanded((expanded) => !expanded)}>
                      {pointEventsExpanded ? "收起" : `展开全部 ${pointEvents.length} 条`}
                    </button>
                  ) : null}
                </div>
                {pointEvents.length > 0 ? (
                  <div className="account-token-event-list">
                    {visiblePointEvents.map((event) => (
                      <div className="account-token-event" key={event.id}>
                        <span>{pointEventLabel(event.type)}</span>
                        <strong className={event.amountPoints >= 0 ? "positive" : "negative"}>{event.amountPoints >= 0 ? "+" : "-"}{formatPoints(Math.abs(event.amountPoints))}</strong>
                        <small>{formatDate(event.createdAt)}{event.orderNo ? ` · 订单 ${event.orderNo}` : event.requestId ? ` · 请求 ${event.requestId}` : event.adminEmail ? ` · 管理员 ${event.adminEmail}` : ""}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="account-token-empty">暂无积分明细。</p>
                )}
              </section>
              <section className="billing-card account-secondary-card">
                <h2>订单记录</h2>
                <div className="billing-table">
                  <div className="billing-table-row head">
                    <span>订单号</span>
                    <span>套餐</span>
                    <span>金额</span>
                    <span>状态</span>
                    <span>创建时间</span>
                  </div>
                  {orders.map((order) => (
                    <button className="billing-table-row as-button" type="button" key={order.id} onClick={() => props.navigate(`/payment/result?order=${encodeURIComponent(order.orderNo)}`)}>
                      <span>{order.orderNo}</span>
                      <span>{order.productName}</span>
                      <span>{order.amountLabel}</span>
                      <span>{statusLabel(order.status)}</span>
                      <span>{formatDate(order.createdAt)}</span>
                    </button>
                  ))}
                  {orders.length === 0 ? <p className="billing-empty">暂无订单。</p> : null}
                </div>
              </section>
              <section className="billing-card account-secondary-card">
                <h2>会员历史</h2>
                <div className="billing-table">
                  <div className="billing-table-row membership-period-row head">
                    <span>来源</span>
                    <span>套餐</span>
                    <span>有效期</span>
                    <span>关联订单/管理员</span>
                    <span>创建时间</span>
                  </div>
                  {periods.map((period) => (
                    <div className="billing-table-row membership-period-row" key={period.id}>
                      <span>{period.source === "ORDER_PAYMENT" ? "订单支付" : "人工调整"}</span>
                      <span>{period.plan.name}</span>
                      <span>{formatDate(period.startedAt)} - {formatDate(period.expiresAt)}</span>
                      <span>{period.orderNo || period.adminEmail || "-"}</span>
                      <span>{formatDate(period.createdAt)}</span>
                    </div>
                  ))}
                  {periods.length === 0 ? <p className="billing-empty">暂无会员历史。</p> : null}
                </div>
              </section>
            </>
          ) : null}
          {profileEditorOpen && accountUser ? (
            <div className="modal-backdrop account-profile-modal-backdrop" role="presentation" onClick={() => setProfileEditorOpen(false)}>
              <section className="auth-modal account-profile-modal" role="dialog" aria-modal="true" aria-labelledby="account-profile-title" onClick={(event) => event.stopPropagation()}>
                <button className="modal-close" type="button" aria-label="关闭" onClick={() => setProfileEditorOpen(false)}>×</button>
                <h2 id="account-profile-title">修改信息</h2>
                <form className="account-profile-form" onSubmit={saveProfile}>
                  <label>
                    <span>昵称</span>
                    <input value={profileNickname} maxLength={80} onChange={(event) => setProfileNickname(event.target.value)} placeholder="请输入昵称" />
                  </label>
                  <label>
                    <span>手机号</span>
                    <input value={profilePhone} onChange={(event) => setProfilePhone(event.target.value)} placeholder="请输入手机号" />
                  </label>
                  <label>
                    <span>邮箱</span>
                    <input value={accountUser.email || "-"} disabled />
                    <small>邮箱修改需要重新验证，暂不在此弹窗中修改。</small>
                  </label>
                  {profileMessage ? <p className="account-profile-form-message">{profileMessage}</p> : null}
                  <div className="account-profile-form-actions">
                    <button className="button button-secondary" type="button" onClick={() => setProfileEditorOpen(false)}>取消</button>
                    <button className="button button-primary" type="submit" disabled={profileSaving}>{profileSaving ? "保存中" : "保存"}</button>
                  </div>
                </form>
              </section>
            </div>
          ) : null}
        </>
      )}
    </BillingShell>
  );
}

export function CheckoutPage(props: BillingPageProps & { planId: string; orderNo: string | null }) {
  const [order, setOrder] = useState<Order | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!props.user || !props.orderNo) return;
    api.order(props.orderNo)
      .then(({ order }) => setOrder(order))
      .catch((error) => setMessage(error instanceof Error ? error.message : "订单加载失败"));
  }, [props.orderNo, props.user]);

  return (
    <BillingShell {...props} title="支付界面" kicker="Checkout">
      {!props.purchaseEnabled ? (
        <section className="billing-card billing-empty-state">
          <CreditCard size={28} />
          <h2>会员购买暂未开放</h2>
          <p>当前官网首版暂不提供在线购买，请返回首页下载或联系工作人员开通。</p>
          <button className="button button-primary" type="button" onClick={() => props.navigate("/")}>返回首页</button>
        </section>
      ) : !props.user ? (
        <section className="billing-card billing-empty-state">
          <CreditCard size={28} />
          <h2>请先登录</h2>
          <button className="button button-primary" type="button" onClick={props.onAuthOpen}>登录</button>
        </section>
      ) : (
        <>
        <PaymentModeNotice paymentMode={props.appSettings.paymentMode} />
        <section className="billing-grid two">
          <article className="billing-card">
            <h2>订单摘要</h2>
            {message ? <p className="billing-message">{message}</p> : null}
            {order ? (
              <>
                <p>订单号：{order.orderNo}</p>
                <p>商品：{order.productName}</p>
                <p>金额：<strong>{order.amountLabel}</strong></p>
                <p>状态：{statusLabel(order.status)}</p>
                <p>过期时间：{formatDate(order.expiresAt)}</p>
              </>
            ) : (
              <p>在线支付正在接入中，当前不会创建支付订单。</p>
            )}
            <button className="button button-primary" type="button" onClick={props.onLaunchNotice}>敬请期待</button>
          </article>
          <article className="billing-card">
            <h2>支付功能准备中</h2>
            <p>会员购买和积分加油包支付暂未开放。后续正式支付接入后，此页面会切换为真实订单支付流程。</p>
          </article>
        </section>
        </>
      )}
    </BillingShell>
  );
}

export function PaymentResultPage(props: BillingPageProps & { orderNo: string | null }) {
  const [order, setOrder] = useState<Order | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    // Result pages poll briefly because real payment callbacks are asynchronous.
    // Mock delayed callbacks use the same behavior, which makes the UI ready for
    // WeChat/Alipay notification timing when live providers are added.
    if (!props.user || !props.orderNo) return;
    let cancelled = false;
    let count = 0;
    async function refresh() {
      if (!props.orderNo) return;
      try {
        const result = await api.order(props.orderNo);
        if (!cancelled) setOrder(result.order);
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "订单状态加载失败");
      }
      count += 1;
      if (count >= 6) window.clearInterval(timer);
    }
    void refresh();
    const timer = window.setInterval(refresh, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [props.orderNo, props.user]);

  const resultIcon = useMemo(() => {
    if (!order) return <AlertTriangle size={32} />;
    if (order.status === "PAID") return <CheckCircle size={32} />;
    if (order.status === "FAILED" || order.status === "CLOSED") return <XCircle size={32} />;
    return <AlertTriangle size={32} />;
  }, [order]);

  return (
    <BillingShell {...props} title="支付结果" kicker="Order status">
      <section className="billing-card billing-empty-state">
        {resultIcon}
        {message ? <p className="billing-message">{message}</p> : null}
        {order ? (
          <>
            <h2>{statusLabel(order.status)}</h2>
            <p>订单号：{order.orderNo}</p>
            <p>商品：{order.productName} / {order.amountLabel}</p>
            <p>支付时间：{formatDate(order.paidAt)}</p>
          </>
        ) : (
          <>
            <h2>等待订单状态</h2>
            <p>如果刚刚触发了延迟回调，状态会在几秒内更新。</p>
          </>
        )}
        <div className="billing-actions-row">
          <button className="button button-primary" type="button" onClick={() => props.navigate("/account")}>返回个人主页</button>
          {order && order.status === "PENDING" ? <button className="button button-secondary" type="button" onClick={props.onLaunchNotice}>支付暂未开放</button> : null}
        </div>
      </section>
    </BillingShell>
  );
}
